import { useMemo, useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform, type MotionValue } from "framer-motion";
import { coverageRails, evidenceRails, type EvidenceTone } from "../content/portfolio";
import { useMediaQuery } from "../hooks/useMediaQuery";

type EvidenceItem = {
  label: string;
  value: string;
  detail: string;
  tone: EvidenceTone;
};

function EvidenceTile({ item, clone = false }: { item: EvidenceItem; clone?: boolean }) {
  return (
    <article className={`evidence-tile evidence-tile--${item.tone}`} aria-hidden={clone || undefined}>
      <div className="evidence-tile__top">
        <span>{item.label}</span>
      </div>
      <strong>{item.value}</strong>
      <p>{item.detail}</p>
    </article>
  );
}

function MotionEvidenceRow({
  items,
  direction,
  progress,
}: {
  items: readonly EvidenceItem[];
  direction: "forward" | "reverse";
  progress: MotionValue<number>;
}) {
  const sequenceWidth = items.length * 356;
  const x = useTransform(
    progress,
    [0, 1],
    direction === "forward"
      ? [-sequenceWidth * 2, -sequenceWidth]
      : [-sequenceWidth, -sequenceWidth * 2],
  );
  const repeatedItems = useMemo(
    () =>
      Array.from({ length: 3 }, (_, clone) =>
        items.map((item, index) => ({ item, index, clone })),
      ).flat(),
    [items],
  );
  return (
    <motion.div
      data-testid={`evidence-row-${direction}`}
      className="evidence-track"
      style={{
        x,
        marginLeft: 0,
        willChange: "transform",
      }}
    >
      {repeatedItems.map(({ item, index, clone }) => (
        <EvidenceTile key={`${direction}-${clone}-${index}`} item={item} clone={clone > 0} />
      ))}
    </motion.div>
  );
}

function MotionEvidenceReels() {
  const reelsRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: reelsRef,
    offset: ["start end", "end start"],
  });

  return (
    <div ref={reelsRef} className="evidence-reels" role="region" aria-label="测试覆盖与质量证据">
      <MotionEvidenceRow items={evidenceRails} direction="forward" progress={scrollYProgress} />
      <MotionEvidenceRow items={coverageRails} direction="reverse" progress={scrollYProgress} />
    </div>
  );
}

function StaticEvidenceRow({
  items,
  direction,
  label,
}: {
  items: readonly EvidenceItem[];
  direction: "forward" | "reverse";
  label: string;
}) {
  return (
    <div
      className="evidence-track evidence-track--static"
      data-testid={`evidence-row-${direction}`}
      role="region"
      aria-label={label}
      tabIndex={0}
    >
      {items.map((item, index) => (
        <EvidenceTile key={`${direction}-${index}`} item={item} />
      ))}
    </div>
  );
}

function StaticEvidenceReels() {
  return (
    <div className="evidence-reels evidence-reels--static">
      <StaticEvidenceRow items={evidenceRails} direction="forward" label="质量基线，共 7 项" />
      <StaticEvidenceRow items={coverageRails} direction="reverse" label="测试覆盖，共 7 项" />
    </div>
  );
}

export function MarqueeSection() {
  const reduceMotion = useReducedMotion();
  const isPhone = useMediaQuery("(max-width: 767px)");
  const useStaticRails = Boolean(reduceMotion) || isPhone;

  return (
    <section
      id="evidence"
      aria-labelledby="evidence-heading"
      className="evidence-section"
    >
      <div className="evidence-intro">
        <div>
          <h2 id="evidence-heading">质量证据<br />在链路上留下痕迹。</h2>
        </div>
        <p className="evidence-intro-copy">
          从接口、Web UI 到移动端和性能场景，执行结果需要被看见、被复测，也要能成为发布判断的依据。
        </p>
      </div>
      {useStaticRails ? <StaticEvidenceReels /> : <MotionEvidenceReels />}
      <div className="evidence-caption">
        <span>TRACE / EVIDENCE / DECISION</span>
        <span>可验证的结果，比口号更接近交付。</span>
      </div>
    </section>
  );
}

export { MarqueeSection as EvidenceSection };
